import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"

import { useUser } from "@hooks/use-user"
import "./LoginCard.css"

export function LoginForm({
  className: _className,
  ...props
}: React.ComponentProps<"div">) {
  const { login, isAuthenticated, loading } = useUser();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const result = await login({ email, password });
    
    if (!result.success) {
      setError(result.message || 'Login failed');
    }
    
    setIsLoading(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-6 items-center justify-center min-h-96" {...props}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5a9db8] mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="signin-container" {...props}>
      <div className="logo">P</div>

      <div className="signin-header">
        <p className="welcome-text">Welcome back</p>
        <p className="login-text">Login to your PecunioAI account</p>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email">Email <span className="required">*</span></label>
          <input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password <span className="required">*</span></label>
          <input
            id="password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            required
          />
          <div className="forgot-password">
            <a href="#">Forgot your password?</a>
          </div>
        </div>

        <button
          type="submit"
          className="signin-btn"
          disabled={isLoading}
        >
          {isLoading ? "Logging in..." : "Login"}
        </button>

        <div className="signup-link">
          Need an account? <a href="https://pecunio.ai/contact">Contact Pecunio</a>
        </div>

        <div className="terms-privacy">
          By using this service, you agree to our <a href="https://pecunio.ai/terms-of-service/">Terms of Service</a> and <a href="https://pecunio.ai/privacy-policy/">Privacy Policy</a>
        </div>
      </form>
    </div>
  );
}